import { t } from 'ttag';

import { isPhoneNumberValid } from '@utils/common';
import { cyrillicWordRegexp, maxDiscountSale, ukrLetterRegexp } from '@constants';
import { Region } from '@hook/useRegion';

/**
 * This function used to check if user email ISP is not blacklisted
 */
function forbiddenEmails(email: string) {
  const blacklistEmailServices = ['mail.ru', 'mail.ua', 'yandex.ru',
    'yandex.ua', 'list.ru', 'inbox.ru', 'bk.ru', 'vk.ru', 'ok.ru'];
  return email && blacklistEmailServices.includes(email.split('@')[1]);
}

/**
 * This function used to check if user input is email address
 */
function isEmail(email: string) {
   
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

/**
 * This function used to check if user input is word
 */
function isWordRegex(value: string) {
  return cyrillicWordRegexp.test(value);
}

/**
 * This function used to check if user input is ukr word
 */
export function isUkraineLetter(value: string) {
  return ukrLetterRegexp.test(value);
}

/**
 * This function used for validating blocked emails in react-final-form
 */
export const validateForbiddenEmail = (value: string) => (
  forbiddenEmails(value) ? t`Неможливо використовувати даний e-mail - ресурс заблокований` : undefined
);

/**
 * This function used for validating that user input is email in react-final-form
 */
export const validateIsEmail = (value?: string) => (
  !value || isEmail(value) ? undefined : t`Невірний e-mail`
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidationType = (value: any) => string | undefined;

/**
 * This function used to compose a few validators into one
 */
export const composeValidators = (
  ...validators: ValidationType[]
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) => (value: any) => validators.reduce(
  (error: string, validator: ValidationType) => error || validator(value),
  undefined,
);

/**
 * This function used to check if input have enough characters in it
 */
export const validateTooShort = (min: number) => (value: string) => (
  value && value.length < min ? t`Вкажіть коментар до скарги (мінімум ${min} символів)` : undefined
);

export const validateMaxLength = (max: number) => (value?: string) => (
  value && value.length > max ? t`Максимальна кількість символів - ${max}` : undefined
);

export const validateMinLength = (min: number) => (value?: string) => (
  value && value.length < min ? t`Мінімальна кількість символів - ${min}` : undefined
);

/**
 * This function used to check required fields
 */
export const required = <T>(value?: T | T[] | null) => {
  if (
    !value
    || (typeof value === 'string' && !value.trim())
    || Array.isArray(value) && value.length === 0
  ) {
    return t`Обов'язково до заповнення`;
  }

  return undefined;
};

export const validatePhoneNumber = (region: Region) => (value?: string) => {
  const phoneFormat = `+${region.phoneCode}${'X'.repeat(region.phoneLength)}`;
  return (!value || isPhoneNumberValid(value, region) ? undefined : t`Невірний формат номера телефону. Введіть номер у форматі ${phoneFormat}`);
};

export const validatePositiveInteger = (value?: string) => {
  const regex = /^\d+$/;

  return !!value && !regex.test(value) ? t`Введіть числове значення, без спецсимволів` : undefined;
};

export const validatePositiveNumberOnly = (value?: string | number) => (
  !value || !Number.isNaN(+value) ? undefined : t`Введіть числове значення, без спецсимволів`
);

export const validateNonZero = (value: string | number) => (
  +value !== 0 ? undefined : t`Введіть число більше 0`
);

export const validateWordRegex = (value?: string) => (
  !value || isWordRegex(value) ? undefined : t`Використовуйте символи кирилиці, будь ласка`
);

export const validateUkraineLetter = (value?: string) => (
  !value || isUkraineLetter(value.toLowerCase()) ? undefined : t`Використовуйте українські символи, будь ласка`
);

export const validateMinDiscountPromotionValue = (min: number) => (value: string) => (
  +value < min ? t`Розмір знижки нижчий за мінімальний для акції` : undefined
);

export const validateMaxDiscountPromotionValue = (value: string) => (
  +value > maxDiscountSale ? t`Максимальний розмір знижки 90%` : undefined
);
